import type { ReactNode } from 'react';
import { PageContainer } from '../PageContainer/PageContainer';
import { SplitLayout } from '../layouts/SplitLayout';
import { TemplateSection } from './Section';

export type ArticleTemplateProps = {
  breadcrumbs?: ReactNode;
  eyebrow?: ReactNode;
  title: ReactNode;
  /** The standfirst under the title. */
  lede?: ReactNode;
  /** One action under the lede (a Button into a prefilled search). The page's one sun action. */
  action?: ReactNode;
  /** A Byline: author and update date. */
  byline?: ReactNode;
  /** A Readout with the headline figure and its basis. */
  readout?: ReactNode;
  /** The article as Prose, with real numbers and links into search. */
  children: ReactNode;
  /** FAQ as an Accordion, with its heading. */
  faq?: { title: ReactNode; content: ReactNode };
  /** A TableOfContents; its action slot holds the page's one alert prompt. */
  toc?: ReactNode;
  className?: string;
};

/**
 * Articles: guides, market reports and research under resources/*, tyoelama/*, arbetsliv/*. One H1,
 * a byline, the headline figure as a Readout, Prose, an FAQ, and sticky contents with one alert prompt.
 * Every figure states its basis.
 */
export function ArticleTemplate({
  breadcrumbs,
  eyebrow,
  title,
  lede,
  action,
  byline,
  readout,
  children,
  faq,
  toc,
  className,
}: ArticleTemplateProps) {
  return (
    <PageContainer className={className}>
      <div className="flex flex-col gap-jc-block py-jc-section">
        {breadcrumbs}
        <SplitLayout aside={toc} asidePosition="right" asideWidth="toc" stickyAside>
          <article className="flex max-w-3xl flex-col gap-jc-block">
            <header className="flex flex-col gap-3">
              {eyebrow ? <span className="type-eyebrow text-jc-ink-secondary">{eyebrow}</span> : null}
              <h1 className="type-display text-jc-ink">{title}</h1>
              {lede ? <p className="max-w-2xl text-base text-jc-ink-secondary">{lede}</p> : null}
              {action ? <div className="flex flex-wrap gap-2">{action}</div> : null}
              {byline}
            </header>
            {readout}
            {children}
            {faq ? (
              <TemplateSection id="faq" title={faq.title}>
                {faq.content}
              </TemplateSection>
            ) : null}
          </article>
        </SplitLayout>
      </div>
    </PageContainer>
  );
}
